/**
 * UK postcode lookup via Melissa GlobalExpress (GEE) proxy — aligned with activity-insurance-react.
 * @see activity-insurance-react/src/services/postcodeLookupService.js
 */

const DEFAULT_LOOKUP_BASE = "https://locktondev.insurforce.co.uk";

export const POSTCODE_REGEX =
	/^([A-PR-UWYZ][0-9][A-HK-Y0-9]?|[A-PR-UWYZ][A-HK-Y0-9][0-9][ABEHMNPRVWXY0-9]?) {0,2}[0-9][ABD-HJLN-UW-Z]{2}$|^GIR 0AA$/i;

export function isValidPostcode(postcode) {
	return POSTCODE_REGEX.test(String(postcode || "").trim());
}

/** Normalise UK postcode for display/storage: uppercase, single space before inward code (last 3 chars). */
export function formatUkPostcodeDisplay(postcode) {
	const compact = String(postcode || "")
		.toUpperCase()
		.replace(/\s+/g, "");
	if (!compact) return "";
	if (compact.length <= 3) return compact;
	return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
}

export function getGeeLookupBaseUrl() {
	const fromEnv = process.env.REACT_APP_POSTCODE_LOOKUP_BASE;
	return (fromEnv && String(fromEnv).trim()) || DEFAULT_LOOKUP_BASE;
}

function mapGeeApiResultToFlatAddress(item) {
	const a = item.Address || {};
	const address1Parts = [a.Premise, a.Building, a.Thoroughfare].filter(
		(x) => !!x && String(x).trim().length > 0
	);
	return {
		description: a.Address || "",
		postcode: a.PostalCode || "",
		address1: address1Parts.join(", "),
		address2: "",
		address3: "",
		flatNumber: a.SubBuilding || "",
		address4: a.Locality || a.City || "",
		address5: a.AdminAreaName || a.ProvinceName || "",
	};
}

/**
 * Fetches address list for a UK postcode (Melissa GlobalExpress via Lockton proxy).
 * @param {string} postcode
 * @param {{ signal?: AbortSignal }} [requestOptions]
 * @returns {Promise<Array<ReturnType<typeof mapGeeApiResultToFlatAddress>>>}
 */
export async function fetchGeeAddressesByPostcode(postcode, requestOptions = {}) {
	const sanitizedPostcode = String(postcode || "")
		.trim()
		.replace(/\s+/g, "");

	if (!sanitizedPostcode) {
		return [];
	}

	const base = getGeeLookupBaseUrl().replace(/\/$/, "");
	const url = `${base}/api/proxy/gee/GlobalExpressFreeForm?ff=${encodeURIComponent(
		sanitizedPostcode
	)}`;

	const response = await fetch(url, {
		method: "GET",
		mode: "cors",
		signal: requestOptions.signal,
	});

	if (!response.ok) {
		throw new Error(`Postcode lookup failed (${response.status})`);
	}

	const data = await response.json();
	const results = data?.Results ?? [];
	return results.map((item) => mapGeeApiResultToFlatAddress(item));
}

/**
 * Maps flat GEE row into the legacy wizard shape used by BasicDetails / PropertyQuestions.
 * @param {ReturnType<typeof mapGeeApiResultToFlatAddress>} act
 */
export function mapGeeFlatAddressToWizardRow(act) {
	const city = act.address4 || "";
	const street = act.address1 || "";
	const formatted_address = [
		street,
		act.address2 || "",
		act.address3 || "",
		city,
	];
	const flatTrim = (act.flatNumber || "").trim();

	return {
		formatted_address,
		sub_building_name: flatTrim,
		building_name: "",
		building_number: "",
		thoroughfare: street,
		town_or_city: city,
		county: act.address5 || "",
		address5: act.address5 || "",
	};
}
